import { groupBy } from "lodash-es";

export interface StyleIndexItem {
  key: string;
  count: number;
  label: string;
}

export interface StyleIndexGroup {
  /** "A"…"Z", or "#" for labels that do not start with a Latin letter. */
  letter: string;
  items: StyleIndexItem[];
}

const NON_LETTER_GROUP = "#";

function indexLetter(label: string): string {
  const first = label.normalize("NFD").replace(/\p{M}/gu, "").charAt(0).toUpperCase();
  return /^[A-Z]$/.test(first) ? first : NON_LETTER_GROUP;
}

function compareLetters(left: string, right: string): number {
  if (left === right) return 0;
  if (left === NON_LETTER_GROUP) return 1;
  if (right === NON_LETTER_GROUP) return -1;
  return left < right ? -1 : 1;
}

/**
 * Groups labelled styles A–Z by the first letter of their translated label (accents folded,
 * `#` last for everything else), sorting each group by label with the locale's collation.
 */
export function groupStyleIndex(
  entries: ReadonlyArray<{ key: string; count: number }>,
  label: (key: string) => string,
  locale: string,
): StyleIndexGroup[] {
  const collator = new Intl.Collator(locale, { sensitivity: "base", numeric: true });
  // `map` returns fresh arrays, so the in-place `sort` never touches the caller's data
  // (`toSorted` is avoided: iOS 15 Safari lacks it and the app ships no polyfill).
  const items = entries
    .map((entry) => ({ ...entry, label: label(entry.key) }))
    .sort((left, right) => collator.compare(left.label, right.label));

  return Object.entries(groupBy(items, (item) => indexLetter(item.label)))
    .map(([letter, groupItems]) => ({ letter, items: groupItems }))
    .sort((left, right) => compareLetters(left.letter, right.letter));
}
